import merge from 'lodash/merge';
import { equals, reverse } from 'ramda';
import type { Node } from 'slate';
import { Editor } from 'slate';

import { mergeWithDefaults } from '../../util';
import type { SuggestedEdit } from '../types';
import { isParagraphElement } from '../types';

/**
 * An aggregation of multiple suggested edits merged into a single value with author and date
 * metadata.
 *
 * The index signature on suggestedProps is used to merge a union type of multiple partials into a
 * single partial.
 */
export interface MergedSuggestedEdits<T extends SuggestedEdit> {
  authorIds: string[];
  date: string;
  suggestedProps: { [K in keyof T['value']]?: T['value'][K] };
}

/**
 * Aggregates suggested edits into a single merged value with author and date metadata.
 * Edits are processed in reverse order so the first edit in the original array wins on conflicts.
 */
export function mergeSuggestedEdits<T extends SuggestedEdit>(
  suggestedEdits: T[]
): MergedSuggestedEdits<T> {
  const authorIds = new Set<string>();
  let lastSuggestionDate: Date | null = null;
  const suggestedProps = {} as MergedSuggestedEdits<T>['suggestedProps'];

  for (const edit of reverse(suggestedEdits)) {
    authorIds.add(edit.authorId);

    const editTimestamp = edit.updatedAt ?? edit.createdAt;
    if (editTimestamp) {
      const editDate = new Date(editTimestamp);
      const isValid = !Number.isNaN(editDate.getTime());
      const isMostRecent = !lastSuggestionDate || editDate > lastSuggestionDate;
      if (isValid && isMostRecent) lastSuggestionDate = editDate;
    }

    merge(suggestedProps, edit.value);
  }

  return {
    authorIds: Array.from(authorIds),
    date: (lastSuggestionDate ?? new Date()).toISOString(),
    suggestedProps,
  };
}

/**
 * Construct a new version of the given node with edits applied.
 *
 * `suggestedEdits` is typed as optional because this is also used as a
 * read-query during normalization, before a freshly inserted node's
 * `suggestedEdits` has been backfilled — a missing array is treated as empty.
 */
export function applySuggestions<T extends { suggestedEdits?: SuggestedEdit[] | null }>(
  node: T
): Omit<T, 'children' | 'suggestedEdits'> {
  // Short-circuit when there are no suggested edits (also a perf win).
  if (!node.suggestedEdits?.length) return node;

  return mergeWithDefaults(mergeSuggestedEdits(node.suggestedEdits).suggestedProps, node);
}

/**
 * Returns all suggested edits that live on a node. Every Aline element and
 * text node carries a `suggestedEdits` array; paragraphs additionally carry
 * pilcrow edits (insertParagraph/deleteParagraph) on
 * `glyphProps.suggestedEdits`, which are included here. Only the editor root
 * carries none.
 *
 * The `Array.isArray` guards are deliberate: the types say `suggestedEdits`
 * is always present, but stored documents can omit it at runtime.
 */
export function getNodeSuggestedEdits(node: Node): SuggestedEdit[] {
  if (Editor.isEditor(node)) return [];
  const ownEdits = Array.isArray(node.suggestedEdits) ? node.suggestedEdits : [];
  if (isParagraphElement(node) && Array.isArray(node.glyphProps?.suggestedEdits)) {
    return [...ownEdits, ...node.glyphProps.suggestedEdits];
  }
  return ownEdits;
}

/**
 * From an array of candidate suggested edits, return the index of a target edit by deep equality,
 * or -1 if the target edit is not found. Optimized to improve performance in documents containing
 * many suggested edits.
 */
export function findSuggestedEditIndex(candidates: SuggestedEdit[], target: SuggestedEdit): number {
  // The set of candidates to check for deep equality in case no candidate matches object equality
  const deepEqualCandidates: { candidate: SuggestedEdit; index: number }[] = [];

  for (let index = 0; index < candidates.length; index++) {
    const candidate = candidates[index];

    // PERF: Check object equality first
    if (candidate === target) return index;

    // PERF: Initial checks to rule out definite non-matches
    if (candidate.groupId === target.groupId && candidate.authorId === target.authorId)
      deepEqualCandidates.push({ candidate, index });
  }

  return deepEqualCandidates.find(({ candidate }) => equals(candidate, target))?.index ?? -1;
}
